import CachedRoundedIcon from '@mui/icons-material/CachedRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { fetchToken } from "../auth/Token";
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

// Styled components
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(2),
  borderStyle: "groove",
  border: "3px",
  borderColor: "grey",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.2)",
  marginTop: "10px",
  minHeight: "50px",
  borderRadius: "10px",
}));

const BankAccountReportTable = ({ data, loadData  }) => {
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [editingValues, setEditingValues] = useState({});
  const [fileUploads, setFileUploads] = useState({});
  


  // For displaying just the filename
  const path = {
    basename: (fullPath) => fullPath?.split(/[\\/]/).pop() || ''
  };

  // Download handler
  const handleDownloadFile = async (report) => {
    try {
      // 1. Handle newly uploaded files (not yet saved to backend)
      if (fileUploads[report.report_id]) {
        const file = fileUploads[report.report_id];
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.name);
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
          URL.revokeObjectURL(url);
          document.body.removeChild(link);
        }, 100);
        return;
      }
  
      // 2. Handle files from backend response
      if (report.statement_file && report.statement_file.content && report.statement_file.filename) {
        // Decode base64 to binary
        const byteCharacters = atob(report.statement_file.content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: report.statement_file.mime_type || 'application/pdf' });
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', report.statement_file.filename);
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          URL.revokeObjectURL(url);
          document.body.removeChild(link);
        }, 100);
        return;
      }
  
      toast.warning('No statement file available for download');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Download failed: ${error.message}`);
    }
  };

  const togglePeriod = (month) => {
    setExpandedPeriods(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };

  const handleValueChange = (reportId, field, value) => {
    setEditingValues(prev => ({
      ...prev,
      [`${reportId}-${field}`]: value
    }));
  };

  const handleFileChange = (reportId, file) => {
    setFileUploads(prev => ({
      ...prev,
      [reportId]: file
    }));
  };

  const handleSaveReconciliation = (report) => {
    const formData = new FormData();
    formData.append('charges', parseFloat(editingValues[`${report.report_id}-charges`]? editingValues[`${report.report_id}-charges`]: report.charges));
    formData.append('report_id', report.report_id);
    formData.append('statements_balance', parseFloat(editingValues[`${report.report_id}-statements_balance`]?editingValues[`${report.report_id}-statements_balance`]: report.statement_balance));
    formData.append('debit', parseFloat(editingValues[`${report.report_id}-debit`]? editingValues[`${report.report_id}-debit`]: report.debit));
    formData.append('credit', parseFloat(editingValues[`${report.report_id}-credit`]?editingValues[`${report.report_id}-credit`]: report.credit));
    formData.append('cod', parseFloat(editingValues[`${report.report_id}-cod`]?editingValues[`${report.report_id}-cod`]: report.cod));
    formData.append('note', editingValues[`${report.report_id}-note`]?editingValues[`${report.report_id}-note`]: report.note);
    if (fileUploads[report.report_id]) {
      formData.append('statement_file', fileUploads[report.report_id]);
    }
    // console.log(report)

    toast.promise(
      api.post(
        '/dashboards/update/bank-account-reconciliation-report',
        formData,
        {
          headers: {
            'Authorization': 'Bearer ' + fetchToken(),
            'Content-Type': 'multipart/form-data'
          }
        }
      ),
      {
        loading: 'Updating reconciliation data...',
        success: (response) => {
          loadData();
          setEditingValues({});
          return response.data
        },
        error: (error) => {
          console.error('Error updating reconciliation:', error);
          
          // Return different messages based on error type if needed
          if (error.response) {
            return error.response.data.message || 'Failed to update reconciliation';
          }
          return 'Network error. Please try again.';
        }
      }
    );
  };

  if (!data || !data.reconciliation_reports || data.reconciliation_reports.length === 0) {
    return <Typography>No reconciliation reports available for this account.</Typography>;
  }


  const currencyFormat = new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
  });

  const formatMonth = (monthString) => {
    const [year, month] = monthString.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  function formatDateToYearMonthDay(date) {
    const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    let suffix = "th";

    if (day === 1 || day === 21 || day === 31) {
    suffix = "st";
    } else if (day === 2 || day === 22) {
    suffix = "nd";
    } else if (day === 3 || day === 23) {
    suffix = "rd";
    }

    return `${day}${suffix} ${month} ${year}`;
}

  return (
    <Box sx={{ overflowX: 'auto', position: 'relative', maxHeight: '80vh', overflowY: 'auto', '&::-webkit-scrollbar': {
      width: '7px',
      height: '7px',
      },
      '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
      },
      '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '10px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
      }, }}>

      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper', pt: 2, pb: 1 }}>
        <Typography variant="h6" gutterBottom fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
          <b>{data.account_name} - Reconciliation Reports</b>
        </Typography>
        <Typography variant="subtitle1" gutterBottom fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
          <b>Current Balance: {currencyFormat.format(data.current_balance)}</b>
        </Typography>
      </Box>
    <Box sx={{ 
      height: 'calc(80vh - 80px)', 
      overflowY: 'auto',
      position: 'relative',
      '&::-webkit-scrollbar': {
        width: '7px',
        height: '7px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
        },
    }}>
      <Table size="small" sx={{ 
          '& thead th': {
            position: 'sticky',
            top: 0,
            zIndex: 3,
            backgroundColor: 'background.paper',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }
        }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Period</b>
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Starting Balance</b>
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 100 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Deposits</b>
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Withdrawals</b>
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 100 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Cash On Hand</b>
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Ending Balance</b>
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 140 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Statement Balance</b>
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 100 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Charges Adjust</b>
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Debit Adjust</b>
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Credit Adjust</b>
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 150 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Balance Adjusted</b>
              </Typography>
            </TableCell>
            <TableCell align="center" sx={{ minWidth: 180 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Statement File</b>
              </Typography>
            </TableCell>
            <TableCell align="center" sx={{ minWidth: 120 }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Actions</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.reconciliation_reports.map((report) => (
            <React.Fragment key={report.report_id}>
              <TableRow sx={{ backgroundColor: expandedPeriods[report.month] ? '#f0f7ff' : 'inherit' }}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <IconButton size="small" onClick={() => togglePeriod(report.month)}>
                      {expandedPeriods[report.month] ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                    </IconButton>
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{formatMonth(report.month)}</b>
                    </Typography>
                    <Typography variant="caption" sx={{ ml: 1 }} fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{report.status}</b>
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{currencyFormat.format(report.starting_balance)}</b>
                    </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{currencyFormat.format(report.deposit_total)}</b>
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ color: 'red'}} fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{currencyFormat.format(Math.abs(report.withdrawal_total))}</b>
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={editingValues[`${report.report_id}-cod`] || report.cod}
                    onChange={(e) => handleValueChange(report.report_id, 'cod', e.target.value)}
                    sx={{ width: '80px' }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{currencyFormat.format(report.ending_balance)}</b>
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    defaultValue={editingValues[`${report.report_id}-statement_balance`] || report.statement_balance}
                    onChange={(e) => handleValueChange(report.report_id, 'statements_balance', e.target.value)}
                    sx={{ width: '120px' }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    defaultValue={editingValues[`${report.report_id}-charges`] || report.charges}
                    onChange={(e) => handleValueChange(report.report_id, 'charges', e.target.value)}
                    sx={{ width: '120px' }}
                  />
                </TableCell>
                {/* Debit Adjustment Field */}
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={editingValues[`${report.report_id}-debit`] || report.debit}
                    onChange={(e) => handleValueChange(report.report_id, 'debit', e.target.value)}
                    sx={{ width: '120px' }}
                  />
                </TableCell>
                
                {/* Credit Adjustment Field */}
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={editingValues[`${report.report_id}-credit`] || report.credit}
                    onChange={(e) => handleValueChange(report.report_id, 'credit', e.target.value)}
                    sx={{ width: '120px' }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography 
                    color={report.discrepancy === report.statement_balance ? 'success.main' : 'error.main'}
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>{currencyFormat.format(report.discrepancy)}</b>
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {/* Hidden file input for upload */}
                  <input
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                    id={`file-upload-${report.report_id}`}
                    type="file"
                    onChange={(e) => handleFileChange(report.report_id, e.target.files?.[0])}
                  />
                  
                  <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                    {/* Upload Button (always shown) */}
                    <label htmlFor={`file-upload-${report.report_id}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        size="small"
                        startIcon={<CloudUploadRoundedIcon />}
                        sx={{ minWidth: '120px' }}
                      >
                        {report.statement_file ? 'Replace File' : 'Upload File'}
                      </Button>
                    </label>

                    {/* File info and download (shown when file exists) */}
                    {report.statement_file && (
                      <Box textAlign="center">
                        <Typography variant="caption" display="block" fontFamily="inherit">
                          {typeof report.statement_file === 'string' ? 
                            path.basename(report.statement_file) : 
                            fileUploads[report.report_id]?.name}
                        </Typography>
                        
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<DownloadRoundedIcon />}
                          onClick={() => handleDownloadFile(report)}
                          sx={{ 
                            mt: 0.5,
                            color: 'primary.main',
                            textTransform: 'none'
                          }}
                        >
                          Download
                        </Button>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={() => handleSaveReconciliation(report)}
                  >
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Save</b>
                    </Typography> 
                  </Button>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={10} style={{ padding: 0 }}>
                  <Collapse in={expandedPeriods[report.month]} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Notes</b>
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={editingValues[`${report.report_id}-note`] || report.note || ''}
                            placeholder='Reason for Debit, Credit, Charges & Notes'
                            onChange={(e) => handleValueChange(report.report_id, 'note', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Transactions</b>
                          </Typography>
                          {report.transactions && report.transactions.length > 0 ? (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Date</b></Typography></TableCell>
                                  <TableCell align="right"><Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Amount</b></Typography></TableCell>
                                  <TableCell><Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Type</b></Typography></TableCell>
                                  <TableCell><Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Narration</b></Typography></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {report.transactions.map((transaction, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{transaction.date ? formatDateToYearMonthDay(new Date(transaction.date)) : 'N/A'}</b>
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography 
                                        color={transaction.type === 'Deposit' ? 'success.main' : 'error.main'}
                                        fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                      >
                                        <b>{currencyFormat.format(transaction.amount)}</b>
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{transaction.type?.charAt(0).toUpperCase() + transaction.type?.slice(1)}</b>
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{transaction.narration}</b>
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Typography variant="body2" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>No transactions for this period</b>
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
    </Box>
  );
};


function BankRecon({ society_id }) {
  const [accounts, setAccounts] = useState([]);
  const [selected_account, setSelected_Account] = useState({});
  const [account_report, setAccount_report] = useState([]);
  const [ecash, setEcash] = useState(false);



  const fetchAccountsSociety = () => {
    toast.promise(
      api.get(`/account/society/all/${society_id}`, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading Bank Accounts",
        success: (response) => {
          setAccounts(response.data?.Accounts);
          return "Bank accounts loaded successfully";
        },
        error: (error) => {
          console.log(error);

          return "An error occurred, please refresh the page";
        },
      }
    );
  };

  useEffect(() => {
    fetchAccountsSociety();
  }, []);

  const reload_account_report = () => {
    const data = new FormData();
    data.append("society_id", society_id);
    if(!ecash){
      data.append("bank_account_id", selected_account.id);
    }
    data.append("ecash", ecash);


    toast.promise(
      api.post("/dashboards/reload/bank-account-report", data, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Reloading Account Data",
        success: (response) => {
          loadData();

          return response.data;
        },
        error: (error) => {
          console.log(error);

          return "An error occurred, please refresh the page";
        },
      }
    );
  };


  const loadData = () => {
    const data = new FormData();
    data.append("account_id", selected_account.id);
    data.append("ecash", ecash);

    toast.promise(
      api.post(
        '/dashboards/bank-account-reconciliation-reports', data,
        {
          headers: {
            Authorization: "Bearer " + fetchToken(),
          },
        }
      ),
      {
        loading: "Loading Report",
        success: (response) => {
          setAccount_report(response.data);

          return "Report loaded successfully";
        },
        error: (error) => {
          console.log(error);

          return "An error occurred, please refresh the page";
        },
      }
    );
  };

  useEffect(() => {
    if (selected_account.id) {
      loadData();
    }
  }, [selected_account]);

  return (
    <Grid container xs={12} md={12} sx={{ mx: 3.5 }}>
      <Grid item xs={12} md={12}>
        <Box
          sx={{
            overflowX: "auto",
            maxWidth: "100%",
            display: "flex",
            justifyContent: "center",
            flexDirection: "row",
            "&::-webkit-scrollbar": {
              height: "7px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
          }}
        >
          <Chip
              label={
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Main Wallet Account</b>
                </Typography>
              }
              size="medium"
              variant={
                ecash
                  ? "filled"
                  : "outlined"
              }
              sx={{
                m: 1,
                boxShadow:
                  "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                backgroundColor:
                  ecash
                    ? "#fff"
                    : "#03045e",
                color:
                  ecash
                    ? "#000"
                    : "#fff",
                "&:hover": {
                  boxShadow:
                    "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                  backgroundColor: "#14213d",
                  color: "#000",
                },
              }}
              onClick={() => {
                  setEcash(true) 
                  setSelected_Account({
                    id: society_id,
                    account: 'Main Wallet Account',
                  })
                }
              }
            />
          {accounts?.map((account) => (
            <Chip
              label={
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{account.account_name}</b>
                </Typography>
              }
              size="medium"
              variant={
                selected_account?.id && selected_account?.id === account.id
                  ? "filled"
                  : "outlined"
              }
              sx={{
                m: 1,
                boxShadow:
                  "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                backgroundColor:
                  selected_account?.id && selected_account?.id === account.id
                    ? "#fff"
                    : "#03045e",
                color:
                  selected_account?.id && selected_account?.id === account.id
                    ? "#000"
                    : "#fff",
                "&:hover": {
                  boxShadow:
                    "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                  backgroundColor: "#14213d",
                  color: "#000",
                },
              }}
              onClick={() => {
                  setEcash(false)
                  setSelected_Account({
                    id: account.id,
                    account: account.account_name,
                  })
                }
              }
            />
          ))}
        </Box>
      </Grid>
      <Grid item xs={12} md={12}>
        {selected_account ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <Typography
              variant="h6"
              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
            >
              <b>Bank Account Report for {selected_account?.account}</b>
            </Typography>
            <Button
              variant="contained"
              size="medium"
              onClick={reload_account_report}
              endIcon={<CachedRoundedIcon />}
              sx={{
                m: 1,
                borderRadius: 7,
                boxShadow:
                  "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                backgroundColor: 'green',
                color:"#fff",
                "&:hover": {
                  boxShadow:
                    "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                  backgroundColor: "#fff",
                  color: "#000",
                },
              }}
            >
              <Typography
                fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
              >
                <b>Reload Report</b>
              </Typography>
            </Button>
          </Box>
        ) : null}
        {selected_account ? (
          <BankAccountReportTable data={account_report} loadData={loadData}/>
        ) : null}
      </Grid>
    </Grid>
  );
}

export default BankRecon;
